import { SimpleLayoutElement } from "@nms-ng2/app/shared/services/layout-manager/layout-manager.models";
import { POSITION_TRANSMISSION_MAP } from "../models/diagram.constants";
import {
    AmplifierDirectionMap,
    CircuitDirection,
    CircuitElement,
    CircuitElementState,
    DeviceElement,
    DeviceType,
    HorizontalPosition
} from "../models/diagram.models";
import { Transmission } from "@nms-ng2/app/modules/topology/links/links.model";
import { Identifier } from "@nms-ng2/app/shared/models/basic.models";

/**
 * Classe auxiliar com funções desenvolvidas para facilitar algumas tarefas do CircuitLayoutBuilder.
 */
export class CircuitLayoutUtils {

    /**
     * Recebe um elemento de layout e vira horizontalmente o elemento.
     *
     * @param element elemento de layout.
     * @returns elemento de layout virado horizontalmente.
     */
    static flip(element: SimpleLayoutElement): SimpleLayoutElement {
        element.style.flipH = !element.style.flipH;

        return element;
    }

    /**
     * Retorna uma transmissão (i.e. RX/TX) de acordo com a posição e direção do circuito.
     *
     * @param position posição horizontal do elemento (i.e. left, right).
     * @param direction direção do circuito (i.e. TOWARDS_Z_END, TOWARDS_A_END).
     * @returns a transmissão desejada.
     */
    static getTransmissionFromDirection(position: HorizontalPosition, direction: CircuitDirection): Transmission {
        const transmissions = POSITION_TRANSMISSION_MAP[position];

        return (direction === CircuitDirection.TOWARDS_Z_END) ? transmissions.top : transmissions.bottom;
    }

    /**
     * Recebe a direção do circuito e retorna uma posição horizontal correspondente.
     *
     * @param direction direção do circuito (i.e. TOWARDS_Z_END, TOWARDS_A_END).
     * @returns posição horizontal (i.e. left, right).
     */
    static getPositionFromDirection(direction: CircuitDirection): HorizontalPosition {
        return (direction === CircuitDirection.TOWARDS_Z_END) ? "right" : "left";
    }

    /**
     * Recebe uma posição horizontal e retorna a posição oposta.
     *
     * @param position posição horizontal (i.e. left, right).
     * @returns posição horizontal oposta.
     */
    static getOppositePosition(position: HorizontalPosition): HorizontalPosition {
        return (position === "left") ? "right" : "left";
    }

    /**
     * Verifica se o mapa de amplificadores possui apenas um amplificador na parte inferior de um endpoint.
     *
     * @param amplifiersMap mapeamento de amplificadores por CircuitDirection.
     * @param deviceType tipo de equipamento
     * @returns TRUE caso satisfaça as condições citadas.
     */
    static isOnlyAmpOnEndpointBottom(amplifiersMap: AmplifierDirectionMap, deviceType: DeviceType): boolean {
        const amplifiers = Object.values(amplifiersMap);
        const isOnEndDevice = deviceType === DeviceType.END;
        const hasOnlyOneAmplifier = amplifiers.length === 1;
        const bottomAmplifier = !!amplifiersMap[CircuitDirection.TOWARDS_A_END];

        return hasOnlyOneAmplifier && bottomAmplifier && isOnEndDevice;
    }

    /**
     * Filtra elementos de circuito de acordo com os estados fornecidos.
     *
     * @param elements elementos de circuito a serem filtrados.
     * @param states estados usados para filtrar.
     * @returns lista de elementos que possuem algum dos estados fornecidos.
     */
    static filterElementsByStates<T extends CircuitElement>(elements: T[], states: CircuitElementState[]): T[] {
        return elements.filter((element) => states.includes(element.state));
    }

    /**
     * Encontra o primeiro elemento de circuito de acordo com os estados fornecidos.
     *
     * @param elements elementos de circuito que serão pesquisados.
     * @param states estados usados para pesquisar.
     * @returns elemento encontrado que possui algum dos estados fornecidos.
     */
    static findElementByStates<T extends CircuitElement>(elements: T[], states: CircuitElementState[]): T {
        return elements.find((element) => states.includes(element.state));
    }

    /**
     * Encontra o primeiro elemento de circuito de acordo com o identificador fornecido.
     *
     * @param elements elementos de circuito que serão pesquisados.
     * @param identifier identificador usado para pesquisar.
     * @returns elemento encontrado que satisfaz o identificador fornecido.
     */
    static findElementById(elements: CircuitElement[], identifier: Identifier): DeviceElement {
        return elements.find((element: DeviceElement) => element.id === identifier);
    }
}
